import { AuthAPI } from '../api/auth';
import { FamiliesApi } from '../api/families';
import { setLocale } from '../i18n/i18n';
import { apiGet } from '../lib/http';
import { authManager } from '../services/AuthManager';
import { FamilyMeResponseData } from '../types/api/families/families';
import { ApiResponse } from '../types/ApiResponse';
import { FamilyWithMembersRead } from '../types/Family';
import {
  AuthorizationContext,
  DailyTaskProgress,
  MeContextResult,
  PendingRewardClaims,
  TaskSummary,
} from '../types/me-context.types';
import { UserRead } from '../types/User';

export class SessionStore {
  private _listeners: Array<() => void> = [];

  private static _instance: SessionStore;

  static get instance(): SessionStore {
    if (!SessionStore._instance) {
      SessionStore._instance = new SessionStore();
    }
    return SessionStore._instance;
  }

  private constructor() {
    // Import AuthStore lazy to avoid circular dependency
    import('./AuthStore').then(({ AuthStore }) => {
      AuthStore.instance.onChange(() => {
        // When auth changes, reload session if authenticated
        if (authManager.getAccessToken()) {
          this.loadSession().catch((err) => console.error('Failed to load session:', err));
        }
      });
    });

    // Watch for URL changes to update locale from query parameter
    window.addEventListener('popstate', () => {
      if (authManager.getAccessToken()) {
        this.loadMeContext().catch((err) => console.warn('Failed to update locale:', err));
      }
    });
  }

  private _family: FamilyWithMembersRead | null = null;

  get family(): FamilyWithMembersRead | null {
    return this._family;
  }

  private _user: UserRead | null = null;

  get user(): UserRead | null {
    return this._user;
  }

  private _authorization: AuthorizationContext | null = null;

  get authorization(): AuthorizationContext | null {
    return this._authorization;
  }

  private _balance: number | null = null;

  get balance(): number | null {
    return this._balance;
  }

  private _taskSummary: TaskSummary = { open: 0, pending: 0 };

  get taskSummary(): TaskSummary {
    return this._taskSummary;
  }

  private _dailyTaskProgress: DailyTaskProgress = {
    open: 0,
    pending: 0,
    completed: 0,
    total: 0,
    percent: 0,
  };

  get dailyTaskProgress(): DailyTaskProgress {
    return this._dailyTaskProgress;
  }

  private _pendingRewardClaims: PendingRewardClaims = {
    items: [],
    count: 0,
    totalPoints: 0,
  };

  get pendingRewardClaims(): PendingRewardClaims {
    return this._pendingRewardClaims;
  }

  private _meContextLoaded = false;

  get meContextLoaded(): boolean {
    return this._meContextLoaded;
  }

  onChange(cb: () => void): () => void {
    this._listeners.push(cb);

    // Return unsubscribe function
    return () => {
      const index = this._listeners.indexOf(cb);
      if (index > -1) {
        this._listeners.splice(index, 1);
      }
    };
  }

  /**
   * Offloads the current user session including user and family data.
   * This method retrieves the user and family information from the respective APIs
   * and updates the internal state accordingly. In case of an error during data retrieval,
   * the method handles the error gracefully – particularly for the "no family yet" onboarding state.
   *
   * @throws {Error} If the session data fails to load, except for FAMILY_NOT_FOUND (404).
   * @return {Promise<void>} A promise that resolves when the session is successfully loaded.
   */
  async loadSession(): Promise<void> {
    try {
      const userRes = await apiGet<ApiResponse<UserRead>>(AuthAPI.currentUser());
      if (!userRes.ok || !userRes.data) {
        throw new Error(userRes.message ?? 'Failed to load current user');
      }
      this._user = userRes.data;

      const familyRes = await apiGet<ApiResponse<FamilyMeResponseData>>(FamiliesApi.myFamily());

      if (!familyRes.data) {
        throw new Error(familyRes.message ?? 'Failed to load current family');
      }

      this._family = familyRes.data.family;

      await this.loadMeContext();
      this.triggerChange();
    } catch (err) {
      // Not every error means the user is logged out.
      // In particular, "no family yet" is a normal onboarding state.
      const status = this.getErrorStatus(err);
      const errorCode = this.getErrorCode(err);

      console.error('Fehler beim Laden der Sitzung:', err);
      if (status === 404 && errorCode === 'FAMILY_NOT_FOUND') {
        this._family = null;
        this._authorization = null;
        this.triggerChange();
        return;
      }
      throw err;
    }
  }

  /**
   * Clears the session store by resetting all user-related data.
   * Resets the user, family, and authorization data to null and triggers a change notification.
   * Also clears the stored locale preference.
   */
  clear(): void {
    this._user = null;
    this._family = null;
    this._authorization = null;
    this._balance = null;
    this._taskSummary = { open: 0, pending: 0 };
    this._dailyTaskProgress = { open: 0, pending: 0, completed: 0, total: 0, percent: 0 };
    this._pendingRewardClaims = { items: [], count: 0, totalPoints: 0 };
    this._meContextLoaded = false;
    localStorage.removeItem('preferred_locale');
    this.triggerChange();
  }

  private triggerChange(): void {
    for (const cb of this._listeners) cb();
  }

  /**
   * Extracts HTTP status code from error object.
   * Checks common property names used by different HTTP libraries.
   */
  private getErrorStatus(err: unknown): number | undefined {
    if (typeof err === 'object' && err !== null) {
      const error = err as Record<string, unknown>;
      if (typeof error.status === 'number') return error.status;
      if (typeof error.status_code === 'number') return error.status_code;
      if (typeof error.statusCode === 'number') return error.statusCode;
    }
    return undefined;
  }

  /**
   * Extracts error code from error object.
   * Checks common property names used by API error responses.
   */
  private getErrorCode(err: unknown): string | undefined {
    if (typeof err === 'object' && err !== null) {
      const error = err as Record<string, unknown>;
      if (typeof error.error_code === 'string') return error.error_code;
      if (typeof error.errorCode === 'string') return error.errorCode;
    }
    return undefined;
  }

  /**
   * Loads the current user's context information from the server.
   *
   * This method retrieves user authorization details and locale preferences from the backend.
   * It checks for a `lang` query parameter in the URL and includes it in the request if present.
   * If a query parameter is found, it's stored in localStorage for persistence across navigation.
   * Otherwise, it uses the locale from localStorage or falls back to backend preference.
   * On successful response, updates the authorization state and applies the effective locale
   * if available. Errors are logged as warnings but do not throw.
   *
   * @async
   * @returns {Promise<void>} A promise that resolves when the context has been loaded and processed.
   *
   * @throws {Error} Throws if the API response is not ok or contains no data (caught and logged internally).
   */
  async loadMeContext(): Promise<void> {
    const queryLang = new URLSearchParams(window.location.search).get('lang');

    // If query parameter is present, save it to localStorage
    if (queryLang) {
      localStorage.setItem('preferred_locale', queryLang);
    }

    // Use query parameter, or fall back to localStorage
    const requestLang = queryLang || localStorage.getItem('preferred_locale') || undefined;

    try {
      const res: ApiResponse<MeContextResult> = await apiGet<ApiResponse<MeContextResult>>(AuthAPI.meContext(), {
        query: requestLang ? { lang: requestLang } : undefined,
      });
      if (!res.ok || !res.data) {
        throw new Error(res.message || 'Failed to load me context');
      }
      this._authorization = res.data.authorization;
      this._balance = res.data.balance ?? null;
      this._dailyTaskProgress = res.data.dailyTaskProgress ?? {
        open: 0,
        pending: 0,
        completed: 0,
        total: 0,
        percent: 0,
      };
      this._taskSummary = {
        open: this._dailyTaskProgress.open,
        pending: this._dailyTaskProgress.pending,
      };
      this._pendingRewardClaims = res.data.pendingRewardClaims ?? { items: [], count: 0, totalPoints: 0 };
      this._meContextLoaded = true;
      const locale = res.data.effective.locale;
      if (locale) {
        await setLocale(locale);
        // Keep localStorage in sync with effective locale from backend
        localStorage.setItem('preferred_locale', locale);
      }
      // Trigger change after authorization is loaded
      this.triggerChange();
    } catch (error) {
      this._meContextLoaded = false;
      console.warn('[i18n] Konnte Locale nicht vom Backend laden:', error);
    }
  }
}
